/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, History, Plus, Wand2 } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { WorkspaceToolbar, WorkspaceToolbarSearch } from '@/components/common/WorkspaceToolbar';
import { TablePaginationBar } from '@/components/common/TablePaginationBar';
import { Button, Card, ConfirmDialog } from '@/components/ui';
import { useMe } from '@/features/settings/hooks/useMe';
import { usePermissions } from '@/hooks/usePermissions';
import type { MeRoleId } from '@/types/api.types';
import {
  useCompanySetupStatuses,
  useDiscardSetupDraft,
  useSetupDrafts,
} from '@/features/setup-wizard/hooks/useSetupWizard';
import { ResumeSetupBanner } from '@/features/setup-wizard/components/ResumeSetupBanner';
import { LoadDemoDataButton } from '@/features/setup-wizard/components/LoadDemoDataButton';
import { DemoDataBanner } from '@/features/setup-wizard/components/DemoDataBanner';
import { draftSetupPath, sortDraftsByRecency } from '@/features/setup-wizard/utils/setupDraft';
import type { SetupDraft } from '@/types/setup-wizard.types';
import { useCompanies } from '../hooks/useCompanies';
import { CompanyList } from './CompanyList';

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

function useScopeAll(): boolean {
  const { data: me } = useMe();
  return useMemo(() => {
    if (!me?.roleId || typeof me.roleId === 'string') return false;
    return (me.roleId as MeRoleId).scope === 'all';
  }, [me]);
}

export function CompaniesPage(): React.ReactElement {
  const navigate = useNavigate();
  const scopeAll = useScopeAll();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const canReadSetup = !permissionsLoading && can('import_setup', 'read');
  const canStartCompanySetup =
    !permissionsLoading &&
    can('companies', 'create') &&
    can('import_setup', 'read') &&
    can('import_setup', 'update');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [draftToDiscard, setDraftToDiscard] = useState<SetupDraft | null>(null);
  const searchDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === '') {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      setDebouncedSearch('');
      return undefined;
    }

    if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(trimmed);
    }, 250);

    return () => {
      if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const listParams = useMemo(
    () => ({
      ...(scopeAll ? {} : { memberOf: 'me' as const }),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [debouncedSearch, scopeAll]
  )
  const { data: companies = [], isLoading, error, refetch } = useCompanies(listParams);
  const { data: setupStatuses = [] } = useCompanySetupStatuses({ enabled: canReadSetup });
  const { data: setupDrafts = [] } = useSetupDrafts({ enabled: canStartCompanySetup });
  const discardDraft = useDiscardSetupDraft();
  // The draft the user was last working on drives the toolbar's primary action, so
  // "Start setup" never silently spawns a second draft alongside an unfinished one.
  const latestDraft = useMemo(
    () => sortDraftsByRecency(setupDrafts)[0] ?? null,
    [setupDrafts]
  );
  const setupStatusByCompanyId = useMemo(
    () => new Map(setupStatuses.map((status) => [status.company.id, status])),
    [setupStatuses]
  );
  const totalPages = Math.max(1, Math.ceil(companies.length / limit));
  const effectivePage = Math.min(page, totalPages);
  const paginatedCompanies = useMemo(
    () => companies.slice((effectivePage - 1) * limit, effectivePage * limit),
    [companies, effectivePage, limit]
  );
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, scopeAll]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Organization', to: '/dashboard' }, { label: 'Companies' }]}
      icon={Building2}
      title="Companies"
      description={
        scopeAll ? 'All companies in your organization.' : 'Companies you are a member of.'
      }
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {companies.length} {companies.length === 1 ? 'company' : 'companies'}
            </span>
          }
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <LoadDemoDataButton />
              {canReadSetup ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => navigate('/companies/setup-wizard/imports')}
                >
                  <History className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="ml-1.5">Import history</span>
                </Button>
              ) : null}
              {canStartCompanySetup ? (
                latestDraft ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate('/companies/setup-wizard/drafts/new')}
                    >
                      <Plus className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="ml-1.5">Start new</span>
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(draftSetupPath(latestDraft))}
                    >
                      <Wand2 className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="ml-1.5">Resume setup</span>
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/companies/setup-wizard/drafts/new')}
                  >
                    <Plus className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="ml-1.5">Start setup</span>
                  </Button>
                )
              ) : null}
            </div>
          }
        >
          <WorkspaceToolbarSearch
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search companies…"
            ariaLabel="Search companies"
            active={Boolean(debouncedSearch)}
          />
        </WorkspaceToolbar>

        <ResumeSetupBanner
          drafts={setupDrafts}
          onDiscard={setDraftToDiscard}
          discardPending={discardDraft.isPending}
        />

        <DemoDataBanner />

        <CompanyList
          companies={paginatedCompanies}
          isLoading={isLoading}
          error={error ?? null}
          onRetry={refetch}
          searchTerm={debouncedSearch}
          setupStatusByCompanyId={setupStatusByCompanyId}
          showSetupStatus={canReadSetup}
          showStartSetupAction={canStartCompanySetup}
        />

        {!isLoading && !error && companies.length > 0 ? (
          <Card className="rounded-xl border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <TablePaginationBar
              page={effectivePage}
              limit={limit}
              total={companies.length}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onLimitChange={(nextLimit) => {
                setLimit(nextLimit);
                setPage(1);
              }}
            />
          </Card>
        ) : null}
      </div>
      <ConfirmDialog
        open={Boolean(draftToDiscard)}
        title="Discard setup draft?"
        message="This removes the saved draft and uploaded import rows. No company or users will be deleted because the draft has not launched."
        confirmLabel="Discard draft"
        variant="danger"
        confirmLoading={discardDraft.isPending}
        onClose={() => setDraftToDiscard(null)}
        onConfirm={() => {
          if (!draftToDiscard) return;
          discardDraft.mutate(draftToDiscard._id, {
            onSuccess: () => setDraftToDiscard(null),
          });
        }}
      />
    </PageShell>
  );
}
